import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { getBases, deleteBase, changeBaseStatus } from "../api/baseApi";
import { confirmDialog, showError } from "../utils/alert";
import Pagination, { DEFAULT_PER_PAGE } from "../components/Pagination";

export default function BasesList() {
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(DEFAULT_PER_PAGE);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const loadBases = async () => {
    setLoading(true);
    try {
      const res = await getBases({ page, per_page: perPage });
      const items = Array.isArray(res.data?.items) ? res.data.items : (res.data || []);
      const totalCount = typeof res.data?.total === "number" ? res.data.total : items.length;
      setBases(items);
      setTotal(totalCount);
    } catch (err) {
      showError("Failed to load bases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBases();
  }, [page, perPage]);

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({ text: "Delete this base?" });
    if (!confirmed) return;
    try {
      await deleteBase(id);
      loadBases();
    } catch (err) {
      showError("Failed to delete base");
    }
  };

  const handleToggleActive = async (base) => {
    try {
      await changeBaseStatus(base.id, !base.is_active);
      loadBases();
    } catch (err) {
      showError("Failed to change base status");
    }
  };

  return (
    <AdminLayout title="Bases">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">Base List</h5>
            <button
              className="btn skedq-accent-btn"
              onClick={() => navigate("/bases/new")}
            >
              Add New Base
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Airport Code</th>
                  <th>Airport Name</th>
                  <th>City</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="7" className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && bases.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No bases found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  bases.map((b, index) => (
                    <tr key={b.id}>
                      <td>{(page - 1) * perPage + index + 1}</td>
                      <td>{b.airport_code || "-"}</td>
                      <td>{b.airport_name || "-"}</td>
                      <td>{b.city || "-"}</td>
                      <td>{b.country || "-"}</td>
                      <td>
                        <span
                          className={
                            "badge " +
                            (b.is_active ? "bg-success" : "bg-secondary")
                          }
                        >
                          {b.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => navigate(`/bases/edit/${b.id}`)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => handleToggleActive(b)}
                          >
                            {b.is_active ? "Deactivate" : "Activate"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(b.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
          />
        </div>
      </div>
    </AdminLayout>
  );
}

